import { useEffect, useState } from 'react';
import { useLoginMutation } from 'redux/features/users/authApi';

import Button from 'components/ui/Button';
import Icon from '../../../components/AppIcon';

const LoginForm = () => {
  const [formData, setFormData] = useState({
    employeeId: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const [login, { error, isLoading }] = useLoginMutation();
  //Admin demo credentials

  useEffect(() => {
    setErrors({
      message: error?.data?.message,
    });
  }, [error]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.employeeId) {
      newErrors.employeeId = 'Employee ID is required';
    } else if (!/^(EMP)?[0-9]+$/.test(formData?.employeeId)) {
      newErrors.employeeId = 'Employee ID must be in format EMP000 or 000';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Validate form first
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    await login({
      employeeId: formData.employeeId,
      password: formData.password,
    }).unwrap();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Employee ID Field */}
      <div>
        <label htmlFor="employeeId" className="block text-sm font-medium text-text-primary mb-2">
          Employee ID
        </label>
        <div className="relative">
          <input
            type="text"
            id="employeeId"
            name="employeeId"
            value={formData?.employeeId}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 pl-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${
              errors?.employeeId ? 'border-error' : 'border-border'
            }`}
            placeholder="Enter your employee ID"
          />
          <Icon
            name="User"
            size={20}
            color={errors?.employeeId ? 'var(--color-error)' : 'var(--color-secondary-400)'}
            className="absolute left-4 top-1/2 transform -translate-y-1/2"
          />
        </div>
        {errors?.employeeId && (
          <p className="mt-1 text-sm text-error flex items-center space-x-1">
            <Icon name="AlertCircle" size={14} />
            <span>{errors?.employeeId}</span>
          </p>
        )}
      </div>

      {/* Password Field */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">
          Password
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            value={formData?.password}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 pl-12 pr-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${
              errors?.password ? 'border-error' : 'border-border'
            }`}
            placeholder="Enter your password"
          />
          <Icon
            name="Lock"
            size={20}
            color={errors?.password ? 'var(--color-error)' : 'var(--color-secondary-400)'}
            className="absolute left-4 top-1/2 transform -translate-y-1/2"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-text-primary transition-colors duration-200"
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
        {errors?.password && (
          <p className="mt-1 text-sm text-error flex items-center space-x-1">
            <Icon name="AlertCircle" size={14} />
            <span>{errors?.password}</span>
          </p>
        )}
      </div>

      {/* Submit Button */}
      <Button
        disabled={isLoading}
        loading={isLoading}
        type="submit"
        className="w-full bg-primary text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium flex items-center justify-center space-x-2"
      >
        <Icon name="LogIn" size={20} />
        <span>Sign In</span>
      </Button>
    </form>
  );
};

export default LoginForm;
